// src/features/admin/components/settings/document-management/components/font-manager/FontStatistics.tsx
import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

interface FontUsageStats {
  fontFamily: string;
  documentCount: number;
  userCount: number;
  lastUsed: string;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

interface UsageByCategory {
  category: string;
  count: number;
  percentage: number;
}

/**
 * Font Statistics Component
 * 
 * Displays font usage analytics:
 * - Most/least used fonts
 * - Usage by document type
 * - Usage trends over time
 * - User adoption rates
 */
const FontStatistics: React.FC = () => {
  const [stats, setStats] = useState<FontUsageStats[]>([]);
  const [categoryStats, setCategoryStats] = useState<UsageByCategory[]>([]);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, [timeRange]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      
      // TODO: Fetch from backend API
      // Mock data
      const mockStats: FontUsageStats[] = [
        {
          fontFamily: 'EdgeDocs Roman',
          documentCount: 1243,
          userCount: 45,
          lastUsed: '2 hours ago',
          trend: 'up',
          trendPercentage: 15
        },
        {
          fontFamily: 'Inter',
          documentCount: 892,
          userCount: 38,
          lastUsed: '5 minutes ago',
          trend: 'up',
          trendPercentage: 8
        },
        {
          fontFamily: 'Courier New',
          documentCount: 234,
          userCount: 12,
          lastUsed: '1 day ago',
          trend: 'down',
          trendPercentage: -5
        },
        {
          fontFamily: 'Arial',
          documentCount: 156,
          userCount: 8,
          lastUsed: '3 days ago',
          trend: 'stable',
          trendPercentage: 0
        }
      ];

      const mockCategoryStats: UsageByCategory[] = [
        { category: 'Serif', count: 1477, percentage: 59 },
        { category: 'Sans-Serif', count: 1048, percentage: 42 },
        { category: 'Monospace', count: 234, percentage: 9 }
      ];

      setStats(mockStats);
      setCategoryStats(mockCategoryStats);
    } catch (error) {
      console.error('Failed to load font statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalDocuments = stats.reduce((sum, stat) => sum + stat.documentCount, 0);
  const totalUsers = new Set(stats.flatMap(s => Array(s.userCount).fill(null))).size;
  const averagePerFont = totalDocuments / (stats.length || 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-statistics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Font Usage Statistics
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Analyze font adoption and usage patterns across your firm.
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {totalDocuments.toLocaleString()}
              </p>
            </div>
            <DocumentTextIcon className="h-8 w-8 text-lex-accent" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.reduce((sum, s) => sum + s.userCount, 0)}
              </p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Fonts in Use</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.length}</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg per Font</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {Math.round(averagePerFont)}
              </p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-amber-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Font Usage Table */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h5 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2 text-lex-accent" />
                Font Usage by Documents
              </h5>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Font
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Trend
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Used
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {stats.map((stat, index) => (
                    <tr key={stat.fontFamily} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        #{index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {stat.fontFamily}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white font-medium">
                          {stat.documentCount.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {((stat.documentCount / totalDocuments) * 100).toFixed(1)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {stat.userCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {stat.trend === 'up' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                            <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                            +{stat.trendPercentage}%
                          </span>
                        )}
                        {stat.trend === 'down' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                            <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                            {stat.trendPercentage}%
                          </span>
                        )}
                        {stat.trend === 'stable' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            Stable
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {stat.lastUsed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Category Distribution */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <ChartBarIcon className="h-5 w-5 mr-2 text-lex-accent" />
              Usage by Category
            </h5>

            <div className="space-y-4">
              {categoryStats.map(category => (
                <div key={category.category}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {category.category}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {category.count.toLocaleString()} docs
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-lex-accent dark:bg-lex-accent h-2 rounded-full transition-all duration-300"
                      style={{ width: `${category.percentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {category.percentage}% of total
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Insights */}
          <div className="bg-lex-accent-soft dark:bg-lex-accent-soft rounded-lg border border-lex-accent dark:border-lex-accent p-4">
            <h5 className="text-sm font-semibold text-lex-accent dark:text-lex-accent mb-3 flex items-center">
              <ChartBarIcon className="h-4 w-4 mr-2" />
              Key Insights
            </h5>
            <ul className="space-y-2 text-sm text-lex-accent dark:text-lex-accent">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>EdgeDocs Roman is your most popular font with 59% adoption</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Serif fonts dominate usage, ideal for court documents</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>45 active users are utilizing custom fonts</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {stats.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No usage data</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Font usage statistics will appear here once documents are created.
          </p>
        </div>
      )}
    </div>
  );
};

export default FontStatistics;

